import { Link } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../../lib/auth'
import { useTours } from '../../lib/tours'
import { useCollection } from '../../lib/firestore'
import { countdown, formatDay, formatDayLong, formatTime, isOnDay } from '../../lib/format'
import type { ItineraryItem, Show, Tour, WithId } from '../../types/models'
import MobileHeader, { MobileScreen, SectionLabel } from '../../components/mobile/MobileHeader'
import Poster from '../../components/Poster'
import Empty from '../../components/Empty'

/**
 * Today.
 *
 * The one screen someone opens at 7am in a hotel corridor: which tour they're
 * on, what's next, what's on today, and where the next show is. Nothing else.
 */
export default function MobileToday() {
  const { uid } = useAuth()
  const { tours, loading } = useTours(uid ?? undefined)

  const now = new Date()
  const current =
    tours.find((tour) => tour.startDate.toDate() <= now && tour.endDate.toDate() >= now) ??
    [...tours].sort(
      (a, b) =>
        Math.abs(a.startDate.toMillis() - now.getTime()) -
        Math.abs(b.startDate.toMillis() - now.getTime()),
    )[0]

  return (
    <>
      <MobileHeader title="Today" back={false} />
      <MobileScreen>
        {loading && !current && <p className="t-caption">Loading…</p>}

        {!loading && !current && (
          <Empty
            title="No tours yet"
            message="Create a tour and everything hangs off it."
            action={{ label: 'Go to Tours', to: '/tours' }}
          />
        )}

        {current && <TodayBody tour={current} />}
      </MobileScreen>
    </>
  )
}

function TodayBody({ tour }: { tour: WithId<Tour> }) {
  const items = useCollection<ItineraryItem>('itineraryItems', [where('tourId', '==', tour.id)])
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])

  const now = new Date()
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)

  const sorted = [...items.data].sort((a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis())
  // An item parked at midnight waiting for a time isn't a credible "next".
  const nextUp = sorted.find((item) => !item.needsTime && item.timeUTC.toDate() >= now)
  const todayItems = sorted.filter((item) => isOnDay(item.timeUTC, now, item.timezone))

  const nextShow = [...shows.data]
    .sort((a, b) => a.date.toMillis() - b.date.toMillis())
    .find((show) => show.date.toDate() >= startOfToday)

  const isRunning = tour.startDate.toDate() <= now && tour.endDate.toDate() >= now

  return (
    <>
      {/* The tour, signalled. Same card as the top of Tours — two near-identical
          cards that differed slightly read as a bug. */}
      <Link
        to={`/tours/${tour.id}`}
        className="panel panel-signalled"
        style={{ display: 'block', padding: 16 }}
      >
        <span className="row" style={{ gap: 7, marginBottom: 12 }}>
          <span className="dot dot-accent" />
          <span className="t-eyebrow accent">{isRunning ? 'On tour' : 'Next tour'}</span>
        </span>

        <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
          <Poster url={tour.posterURL} width={72} />
          <div className="stack" style={{ gap: 5, minWidth: 0 }}>
            <span className="t-display-m" style={{ lineHeight: 1.05 }}>
              {tour.tourName}
            </span>
            <span className="t-body dim truncate">{tour.artist}</span>
            <span className="t-spec-caption faint">
              {formatDay(tour.startDate)} – {formatDay(tour.endDate)}
            </span>
          </div>
        </div>
      </Link>

      {nextUp && (
        <section className="stack" style={{ gap: 8 }}>
          <SectionLabel>Next up</SectionLabel>
          <Link
            to={`/tours/${tour.id}/schedule`}
            className="panel"
            style={{ display: 'block', padding: 16 }}
          >
            <div className="row" style={{ gap: 14 }}>
              <div className="stack" style={{ gap: 1, flex: 'none' }}>
                <span className="t-timecode" style={{ fontSize: '1.4rem' }}>
                  {formatTime(nextUp.timeUTC, nextUp.timezone)}
                </span>
                <span className="t-spec-caption faint">
                  {formatDay(nextUp.timeUTC, nextUp.timezone)}
                </span>
              </div>
              <span
                aria-hidden
                style={{ width: 1, alignSelf: 'stretch', background: 'var(--hairline)' }}
              />
              <div className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                <span className="truncate" style={{ fontWeight: 600 }}>
                  {nextUp.title}
                </span>
                {nextUp.subtitle && (
                  <span className="t-caption truncate">{nextUp.subtitle}</span>
                )}
              </div>
              <span className="t-spec-caption accent" style={{ flex: 'none' }}>
                {countdown(nextUp.timeUTC)}
              </span>
            </div>
          </Link>
        </section>
      )}

      <section className="stack" style={{ gap: 8 }}>
        <SectionLabel
          trailing={
            todayItems.length > 0 ? (
              <span className="t-spec-caption faint">{todayItems.length}</span>
            ) : undefined
          }
        >
          Today
        </SectionLabel>
        {todayItems.length === 0 ? (
          <p className="panel panel-pad t-caption">Nothing scheduled today.</p>
        ) : (
          <div className="panel rows">
            {todayItems.map((item) => (
              <div key={item.id} className="row" style={{ padding: '13px 16px', gap: 12 }}>
                <span className="t-timecode" style={{ minWidth: 52 }}>
                  {item.needsTime ? '--:--' : formatTime(item.timeUTC, item.timezone)}
                </span>
                <span className="grow truncate">{item.title}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {nextShow && (
        <section className="stack" style={{ gap: 8 }}>
          <SectionLabel>Next show</SectionLabel>
          <Link
            to={`/tours/${tour.id}/shows/${nextShow.id}`}
            className="panel"
            style={{ display: 'block', padding: 16 }}
          >
            <div className="spread" style={{ gap: 12, alignItems: 'flex-start' }}>
              <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{nextShow.city}</span>
                <span className="t-caption truncate">{nextShow.venueName}</span>
              </div>
              <div className="stack" style={{ gap: 2, alignItems: 'flex-end', flex: 'none' }}>
                <span className="t-spec-caption">
                  {formatDay(nextShow.date, nextShow.timezone)}
                </span>
                <span className="t-spec-caption accent">{countdown(nextShow.date)}</span>
              </div>
            </div>
            {nextShow.headlinerSetTime && (
              <div
                className="t-spec-caption faint"
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: '1px solid var(--hairline)',
                }}
              >
                {formatDayLong(nextShow.date, nextShow.timezone)} · set{' '}
                {formatTime(nextShow.headlinerSetTime, nextShow.timezone)}
              </div>
            )}
          </Link>
        </section>
      )}
    </>
  )
}
